import { Injectable } from "@angular/core";
import { BaseApiService } from "../../shared/utils/base-api.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import { MatSnackBar } from "@angular/material/snack-bar";
import { map, Observable } from "rxjs";
import { APIResponse } from "../../core/models/api.response.model";
import {
    Channel,
    ChannelFilter,
    ChatAgentLogging,
    Message,
    MessageTemplate,
    ProactiveMessageContext,
    QueryGPTLog,
    QueryGPTLogsFilter
} from "../../core/models/messaging.models";
import { ScheduledMessage } from "../chat/chat.types";
import { UserService } from "../../core/user/user.service";

@Injectable({
    providedIn: "root",
})
export class MessagingAPIService extends BaseApiService {
    constructor(private _httpClient: HttpClient, matSnackBar: MatSnackBar, private _userService: UserService) {
        super(matSnackBar);
    }

    fetchChannelById(id: string): Observable<Channel> {
        return this._httpClient
            .get<Channel>(
                `${this.BASE_URL}/messaging/user_channel_by_id?channel_id=${id}`,
                {}
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
                map((response: APIResponse<Channel>) => {
                    return response.data;
                })
            );
    }

    fetchChannels(
        limit: number,
        offset: number
    ): Observable<{
        user_channels: Channel[];
        is_last_page: boolean;
    }> {
        return this._httpClient
            .get<APIResponse<{
                user_channels: Channel[];
                is_last_page: boolean;
            }>>(
                `${this.BASE_URL}/messaging/user_channels`,
                {
                    params: {
                        limit: limit,
                        offset: offset
                    }
                }
            )
            .pipe(
                map((response: APIResponse<{
                    user_channels: Channel[];
                    is_last_page: boolean;
                }>) => {
                    return response.data;
                })
            );
    }

    fetchMessages(
        channelId: string,
        initialChannelMessageId?: string,
        beforeLimit?: number,
        afterLimit?: number
    ): Observable<{
        channel_messages: Message[];
        is_last_page: boolean;
    }> {
        let params = new HttpParams({
            fromObject: {
                channel_id: channelId,
                before_limit: beforeLimit,
                after_limit: afterLimit,
            }
        });
        if (initialChannelMessageId) {
            params = params.append('initial_channel_message_id', initialChannelMessageId);
        }
        return this._httpClient
            .get<APIResponse<{
                channel_messages: Message[];
                is_last_page: boolean;
            }>>(
                `${this.BASE_URL}/messaging/channel_messages/v2`,
                {
                    params: params
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
                map((response: APIResponse<{
                    channel_messages: Message[];
                    is_last_page: boolean;
                }>) => {
                    return response.data;
                })
            );
    }

    sendMessage(
        channelId: string,
        text: string,
        message_type: number,
        formData: FormData
    ): Observable<Message> {
        if (formData == null) {
            formData = new FormData();
        }
        if (text != null) {
            formData.append('text', text);
        }
        formData.append('channel_id', channelId);
        formData.append('type', message_type.toString());

        return this._httpClient
            .post<any>(`${this.BASE_URL}/messaging/send_message/`, formData)
            .pipe(
                this.errorHandler,
                this.announceFailureResponse,
                map((response: APIResponse<Message>) => {
                    if (response.success == true)
                        return response.data as Message;
                    else return null;
                })
            );
    }

    sendFreeCareCommunityInviteLink(
        freeCareEnrolmentId: string
    ): Observable<boolean> {
        return this._httpClient
            .post<any>(`${this.BASE_URL}/messaging/send_free_care_enrolment_wa_community_invite_message`, {
                free_care_enrolment_id: freeCareEnrolmentId,
            })
            .pipe(
                this.announceResponse,
                this.errorHandler,
                this.announceFailureResponse,
                map((response: APIResponse<any>) => {
                    return response.success;
                })
            );
    }


    scheduleMessage(channelId: string, scheduleSendTime: string, messageText: string): Observable<boolean> {
        return this._httpClient
            .post<any>(`${this.BASE_URL}/messaging/scheduled_message`, {
                message_text: messageText,
                schedule_send_time: scheduleSendTime,
                channel_id: channelId,
            })
            .pipe(
                this.errorHandler,
                this.announceFailureResponse,
                map((response: APIResponse<any>) => {
                    return response.success;
                })
            );
    }

    editScheduledMessage(scheduledMessageId: string, scheduleSendTime: string, messageText: string): Observable<boolean> {
        return this._httpClient
            .patch<any>(`${this.BASE_URL}/messaging/scheduled_message`, {
                message_text: messageText,
                schedule_send_time: scheduleSendTime,
                scheduled_message_id: scheduledMessageId,
            })
            .pipe(
                this.errorHandler,
                this.announceFailureResponse,
                map((response: APIResponse<any>) => {
                    return response.success;
                })
            );
    }

    deleteScheduledMessage(scheduledMessageId: string): Observable<boolean> {
        return this._httpClient
            .delete<any>(`${this.BASE_URL}/messaging/scheduled_message`, {
                body: { scheduled_message_id: scheduledMessageId },
            })
            .pipe(
                this.errorHandler,
                this.announceFailureResponse,
                map((response: APIResponse<any>) => {
                    return response.success;
                })
            );
    }

    getScheduledMessages(channelId: string): Observable<ScheduledMessage[]> {
        let params = new HttpParams();
        params = params.append('channel_id', channelId)

        return this._httpClient
            .get<APIResponse<ScheduledMessage[]>>(
                `${this.BASE_URL}/messaging/scheduled_message`,
                {
                    params: params
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
                map((value: APIResponse<ScheduledMessage[]>) => value.data)
            );
    }

    markQueryResolved(channelId: string, overrideValidation: boolean): Observable<Channel> {
        return this._httpClient
            .post<any>(`${this.BASE_URL}/messaging/resolve_chat_ticket_v2`, {
                channel_id: channelId,
                override_validation: overrideValidation ?? false
            })
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceResponse,
                map((response: APIResponse<Channel>) => {
                    return response.data;
                })
            );
    }

    transferQuery(channelId: string): Observable<Channel> {
        return this._httpClient
            .post<any>(
                `${this.BASE_URL}/careplan/care_crm/transfer_chat_ticket`,
                {
                    channel_id: channelId,
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceResponse,
                map((response: APIResponse<Channel>) => {
                    return response.data;
                })
            );
    }

    snoozeProactiveMode(channelId: string, snoozeTill: string): Observable<boolean> {
        return this._httpClient
            .post<any>(
                `${this.BASE_URL}/messaging/snooze_proactive_mode`,
                {
                    channel_id: channelId,
                    snoozed_till: snoozeTill,
                }
            )
            .pipe(
                this.errorHandler,
                this.announceFailureResponse,
                map((response: APIResponse<any>) => {
                    return response.success;
                })
            );
    }

    /* TODO(fix API signature) */
    searchChannels(
        filter: ChannelFilter,
        limit: number,
        offset: number
    ): Observable<{
        channels: Channel[];
        is_last_page: boolean;
    }> {
        console.log('messaging api service filters,limit,offset ->', filter, limit, offset);
        let params: HttpParams = new HttpParams();
        params = params.set('limit', limit);
        params = params.set('offset', offset);
        if (filter.query_opened) {
            params = params.set(
                'query_opened',
                JSON.stringify(filter.query_opened)
            );
        }
        if (filter.all_open_queries) {
            params = params.set(
                'all_open_queries',
                JSON.stringify(filter.all_open_queries)
            );
        }
        if (filter.chat_agent_response_loaded) {
            params = params.set(
                'chat_agent_response_loaded',
                JSON.stringify(true)
            );
        }
        if (filter.program_tier) {
            params = params.set(
                'program_tier',
                JSON.stringify(filter.program_tier)
            );
        }
        if (filter.program_type) {
            params = params.set(
                'program_type',
                JSON.stringify(filter.program_type)
            );
        }
        if (filter.lead_selling_stage) {
            params = params.set(
                'lead_selling_stage',
                JSON.stringify(filter.lead_selling_stage)
            );
        }
        if (filter.lead_owner) {
            params = params.set(
                'lead_owner',
                JSON.stringify(filter.lead_owner)
            );
        }
        if (filter.latest_query_classification) {
            params = params.set(
                'latest_query_classification',
                JSON.stringify(filter.latest_query_classification)
            );
        }
        if (filter.paid_patients !== undefined) {
            params = params.set(
                'paid_patients',
                JSON.stringify(filter.paid_patients)
            );
        }
        if (filter.proactive_mode !== undefined) {
            params = params.set(
                'proactive_mode',
                JSON.stringify(filter.proactive_mode)
            );
        }
        if (filter.search_query !== undefined) {
            params = params.set('search_query', filter.search_query);
        }
        if (filter.doctor_stage !== undefined) {
            params = params.set('doctor_stage', JSON.stringify(filter.doctor_stage));
        }
        if (filter.is_assistant){
            params = params.set('is_assistant', JSON.stringify(filter.is_assistant));
        }
        return this._httpClient
            .get<APIResponse<{
                channels: Channel[];
                is_last_page: boolean;
            }>>(
                `${this.BASE_URL}/messaging/channel_search`,
                { params: params }
            )
            .pipe(
                this.errorHandler,
                map((response: APIResponse<{
                    channels: Channel[];
                    is_last_page: boolean;
                }>) => {
                    return response.data;
                })
            );
    }

    /* TODO(implement this) */
    markChannelRead(channelId: string): Observable<any> {
        return this._httpClient
            .post<any>(`${this.BASE_URL}/messaging/mark_channel_read`, {
                channel_id: channelId,
            })
            .pipe(
                map((response: any) => {
                    return response['success'];
                })
            );
    }

    fetchQueryGPTLogs(filter: QueryGPTLogsFilter, limit: number, offset: number): Observable<{
        logs: QueryGPTLog[],
        is_last_page: boolean,
    }> {

        let params = new HttpParams();

        params = params.append('offset', offset)
        params = params.append('limit', limit);

        if (filter.ticket_created) {
            params = params.append('ticket_created', filter.ticket_created)
        }
        if (filter.evaluation_status) {
            params = params.append('evaluation_status', JSON.stringify(filter.evaluation_status))
        }
        if (filter.smart_eval_status) {
            params = params.append('smart_eval_status', filter.smart_eval_status)
        }
        if (filter.classification) {
            params = params.append('classification', JSON.stringify(filter.classification))
        }
        if (filter.chat_agent_classification) {
            params = params.append('chat_agent_classification', JSON.stringify(filter.chat_agent_classification))
        }
        if (filter.phone_number) {
            params = params.append('patient_phone', filter.phone_number)
        }
        if (filter.careplan_type) {
            params = params.append('program_type', JSON.stringify(filter.careplan_type))
        }
        if (filter.careplan_tier) {
            params = params.append('program_tier', JSON.stringify(filter.careplan_tier))
        }
        if ((filter.tags?.value?.length ?? 0) > 0) {
            params = params.append('tags_' + filter.tags.operator, JSON.stringify(filter.tags.value.map(tag => tag.id)))
        }
        if (filter.is_referred !== undefined) {
            params = params.append('is_referred', filter.is_referred)
        }

        return this._httpClient
            .get<APIResponse<{
                logs: QueryGPTLog[],
                is_last_page: boolean,
            }>>(
                `${this.BASE_URL}/messaging/user_gpt_logs`,
                {
                    params: params
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
                map((value: APIResponse<{
                    logs: QueryGPTLog[],
                    is_last_page: boolean,
                }>) => value.data)
            );
    }

    getQueryGptLogById(queryGPTLogId: string): Observable<QueryGPTLog> {
        let params: HttpParams = new HttpParams();
        if (queryGPTLogId) {
            params = params.append('id', queryGPTLogId);
        }
        return this._httpClient
            .get<APIResponse<QueryGPTLog>>(
                `${this.BASE_URL}/messaging/user_gpt_log_by_id`,
                {
                    params: params
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
                map((value: APIResponse<QueryGPTLog>) => value.data)
            );
    }


    getChatAgentLogsById(chatTicketID: string): Observable<ChatAgentLogging> {
        let params: HttpParams = new HttpParams();
        if (chatTicketID) {
            params = params.append('patient_chat_ticket_gpt_log_id', chatTicketID);
        }
        return this._httpClient
            .get<APIResponse<ChatAgentLogging>>(
                `${this.BASE_URL}/messaging/chat_agent_logs`,
                {
                    params: params
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
                map((value: APIResponse<ChatAgentLogging>) => value.data)
            );
    }

    getQueryGptLogByMessageId(channelMessageId: string): Observable<QueryGPTLog> {
        let params: HttpParams = new HttpParams();
        if (channelMessageId) {
            params = params.append('channel_message_id', channelMessageId);
        }
        return this._httpClient
            .get<APIResponse<QueryGPTLog>>(
                `${this.BASE_URL}/messaging/user_gpt_log_by_message_id`,
                {
                    params: params
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
                map((value: APIResponse<QueryGPTLog>) => value.data)
            );
    }

    markGptLogTechStatus(logId: string, status: string, agent_classification: string, agent_action_classification: string, comments: string, ideal_response: string): Observable<boolean> {
        let data = {
            patient_gpt_log_id: logId,
        };
        if (status) {
            data['tech_evaluation_status'] = status;
        }
        if (agent_classification) {
            data['agent_classification'] = agent_classification;
        }
        if (agent_action_classification) {
            data['agent_action_classification'] = agent_action_classification;
        }
        if (comments) {
            data['comments'] = comments;
        }
        if (ideal_response) {
            data['ideal_response'] = ideal_response;
        }
        return this._httpClient
            .post<any>(`${this.BASE_URL}/messaging/patient_chat_ticket_gpt_log_tech_tagging`, data)
            .pipe(
                this.announceFailureResponse,
                this.errorHandler,
                map((response: APIResponse<any>) => {
                    return response.success;
                })
            );
    }


    sendMessageTemplate(
        channelIds: string[],
        template: MessageTemplate,
        isDefaultMessage: boolean
    ): Observable<boolean> {
        let body = {
            channel_ids: channelIds,
            is_default_message: isDefaultMessage,
            ...template
        };
        return this._httpClient
            .post<any>(
                `${this.BASE_URL}/messaging/send_message_template/`,
                body
            )
            .pipe(
                this.errorHandler,
                this.announceFailureResponse,
                map((response: any) => {
                    return response['success'];
                })
            );
    }

    getPatientChannels(patientID: string): Observable<Channel[]> {
        return this._httpClient
            .get<APIResponse<QueryGPTLog>>(
                `${this.BASE_URL}/messaging/patient_channels`,
                {
                    params: {
                        patient_id: patientID
                    }
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
                map((value: APIResponse<Channel[]>) => value.data)
            );
    }

    getDoctorChannels(doctorID: string): Observable<{ doctor_channel: Channel, assistant_channels: Channel[] }> {
        return this._httpClient
            .get<APIResponse<QueryGPTLog>>(
                `${this.BASE_URL}/careplan/bd_crm/doctor_bd_waba_channels`,
                {
                    params: {
                        doctor_id: doctorID
                    }
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
                map((value: APIResponse<{ doctor_channel: Channel, assistant_channels: Channel[] }>) => value.data)
            );
    }


    getProactiveMessageContext(channelMessageID: string): Observable<APIResponse<ProactiveMessageContext>> {
        return this._httpClient
            .get<APIResponse<ProactiveMessageContext>>(
                `${this.BASE_URL}/messaging/magic_button/get_proactive_message_suggestion`,
                {
                    params: {
                        channel_message_id: channelMessageID
                    }
                }
            )
            .pipe(
                this.delayedRetry(),
                this.errorHandler,
                this.announceFailureResponse,
            );
    }


    markProactiveMessageSuggestionRejected(
        proactiveMessageSuggestionID: string,
        reason: string,
    ): Observable<APIResponse<any>> {
        return this._httpClient
            .post<any>(
                `${this.BASE_URL}/messaging/magic_button/mark_proactive_message_suggestion_rejected`,
                {
                    proactive_message_suggestion_id: proactiveMessageSuggestionID,
                    rejection_reason: reason,
                }
            )
            .pipe(
                this.errorHandler,
                this.announceFailureResponse
            );
    }


    sendProactiveMessageSuggestion(
        proactiveMessageSuggestionID: string,
    ): Observable<APIResponse<any>> {
        return this._httpClient
            .post<any>(
                `${this.BASE_URL}/messaging/magic_button/send_proactive_message_suggestion`,
                {
                    proactive_message_suggestion_id: proactiveMessageSuggestionID
                }
            )
            .pipe(
                this.errorHandler,
                this.announceFailureResponse,
            );
    }


    chatAgentReplyFeedback(
        channelID: string,
        comments: string,
    ): Observable<boolean> {
        return this._httpClient
            .post<any>(
                `${this.BASE_URL}/messaging/chat_agent_reply_feedback`,
                {
                    channel_id: channelID,
                    comments: comments,
                }
            )
            .pipe(
                this.errorHandler,
                this.announceFailureResponse,
                map((response: any) => {
                    return response.success;
                })
            );
    }

    generateMemoriesForEnrolment(patientID: string): Observable<boolean> {
        return this._httpClient
            .post<any>(`${this.BASE_URL}/messaging/magic_button/generate_memories_for_enrolment`, {
                patient_id: patientID
            })
            .pipe(
                map((response: APIResponse<any>) => {
                    return response.success;
                })
            );
    }


    markChannelMessageQualityFeedback(channelMessageId: string, flagType: 'yellow' | 'red', explanation?: string): Observable<boolean> {
        return this._httpClient
            .post<any>(`${this.BASE_URL}/messaging/mark_channel_message_quality_flag`, {
                channel_message_id: channelMessageId,
                flag_type: flagType,
                explanation: explanation,
            })
            .pipe(
                this.errorHandler,
                this.announceResponse,
                map((response: APIResponse<any>) => {
                    return response.success;
                })
            );
    }

}
